export type StoryMediaKind='image'|'video';
export const MAX_STORY_IMAGE_BYTES=4*1024*1024;
export const MAX_STORY_VIDEO_BYTES=64*1024*1024;
export const MAX_STORY_PIXELS=16_777_216;
let residentBytes=0,residentPixels=0;
/** Conservative logical reservation for fetch buffers and decoded canvases across all Story items. */
export function acquireStoryMediaLease(kind:StoryMediaKind):()=>void {
  const bytes=kind==='image'?MAX_STORY_IMAGE_BYTES:MAX_STORY_VIDEO_BYTES;
  const pixels=MAX_STORY_PIXELS;
  if(residentBytes+bytes>256*1024*1024||residentPixels+pixels>64*1024*1024)
    throw Error('Simultaneous Story media exceeds browser budget');
  residentBytes+=bytes;residentPixels+=pixels;
  let released=false;
  return ()=>{if(!released){released=true;residentBytes-=bytes;residentPixels-=pixels;}};
}

export function decodeStoryDataUri(src:string,kind:StoryMediaKind):Uint8Array|null {
  const match=/^data:([^;,]+);base64,([A-Za-z0-9+/]+={0,2})$/i.exec(src);
  if(!match||!match[2]||match[2].length%4!==0)return null;
  const mime=match[1].toLowerCase();
  if(kind==='image'&&!['image/png','image/jpeg','image/webp'].includes(mime))return null;
  if(kind==='video'&&!['video/mp4','video/webm'].includes(mime))return null;
  const limit=kind==='image'?MAX_STORY_IMAGE_BYTES:6*1024*1024;
  if(match[2].length*3/4>limit+2)return null;
  try {
    const raw=atob(match[2]);
    if(raw.length>limit||btoa(raw)!==match[2])return null;
    const bytes=Uint8Array.from(raw,char=>char.charCodeAt(0));
    return hasStoryMediaSignature(bytes,mime)?bytes:null;
  } catch{return null;}
}

export function storyImageDimensions(bytes:Uint8Array):{width:number;height:number}|null {
  const read16=(at:number)=>bytes[at]*256+bytes[at+1];
  const read32=(at:number)=>(bytes[at]*0x1000000+bytes[at+1]*0x10000+bytes[at+2]*256+bytes[at+3])>>>0;
  if(bytes.length>=24&&bytes[0]===137&&bytes[1]===80&&bytes[2]===78&&bytes[3]===71)
    return bytes[4]===13&&bytes[5]===10&&bytes[6]===26&&bytes[7]===10&&
      String.fromCharCode(...bytes.slice(12,16))==='IHDR'?{width:read32(16),height:read32(20)}:null;
  if(bytes.length>=30&&String.fromCharCode(...bytes.slice(0,4))==='RIFF'&&
      String.fromCharCode(...bytes.slice(8,12))==='WEBP'){
    const type=String.fromCharCode(...bytes.slice(12,16));
    if(type==='VP8X')return {width:1+bytes[24]+(bytes[25]<<8)+(bytes[26]<<16),height:1+bytes[27]+(bytes[28]<<8)+(bytes[29]<<16)};
    if(type==='VP8L')return {width:1+(((bytes[22]&0x3f)<<8)|bytes[21]),height:1+(((bytes[24]&0x0f)<<10)|(bytes[23]<<2)|((bytes[22]&0xc0)>>6))};
    if(type==='VP8 '&&bytes[23]===0x9d&&bytes[24]===0x01&&bytes[25]===0x2a)
      return {width:(bytes[26]+(bytes[27]<<8))&0x3fff,height:(bytes[28]+(bytes[29]<<8))&0x3fff};
  }
  if(bytes.length>4&&bytes[0]===0xff&&bytes[1]===0xd8){
    let at=2;
    while(at+4<bytes.length){
      if(bytes[at]!==0xff)return null;
      while(bytes[at]===0xff)at++;
      const marker=bytes[at++];
      if(marker===0xd9||marker===0xda)break;
      if(at+2>bytes.length)return null;
      const size=read16(at);
      if(size<2||at+size>bytes.length)return null;
      if(([0xc0,0xc1,0xc2,0xc3,0xc5,0xc6,0xc7,0xc9,0xca,0xcb,0xcd,0xce,0xcf] as number[]).includes(marker))
        return size>=7?{width:read16(at+5),height:read16(at+3)}:null;
      at+=size;
    }
  }
  return null;
}

function completePng(bytes:Uint8Array):boolean {
  if(bytes.length<45)return false;
  const view=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  let at=8,first=true,hasData=false;
  while(at+12<=bytes.length){
    const size=view.getUint32(at),type=String.fromCharCode(...bytes.slice(at+4,at+8));
    if(size>bytes.length-at-12)return false;
    if(first&&(type!=='IHDR'||size!==13))return false;
    if(type==='IDAT'&&size>0)hasData=true;
    at+=12+size;first=false;
    if(type==='IEND')return size===0&&hasData&&at===bytes.length;
  }
  return false;
}
function completeJpeg(bytes:Uint8Array):boolean {
  if(bytes.length<20||bytes.at(-2)!==0xff||bytes.at(-1)!==0xd9)return false;
  let at=2,hasFrame=false;
  while(at+4<bytes.length-2){
    if(bytes[at]!==0xff)return false;
    while(bytes[at]===0xff)at++;
    const marker=bytes[at++],size=bytes[at]*256+bytes[at+1];
    if(size<2||at+size>bytes.length-2)return false;
    if([0xc0,0xc1,0xc2,0xc3,0xc5,0xc6,0xc7,0xc9,0xca,0xcb,0xcd,0xce,0xcf].includes(marker))hasFrame=true;
    if(marker===0xda)return hasFrame&&size>=6&&at+size<bytes.length-2;
    at+=size;
  }
  return false;
}
function completeWebp(bytes:Uint8Array):boolean {
  if(bytes.length<30)return false;
  const view=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  if(view.getUint32(4,true)+8!==bytes.length)return false;
  const chunks=(start:number,end:number,allowFrames:boolean):boolean=>{
    let at=start,hasPayload=false;
    while(at+8<=end){
      const type=String.fromCharCode(...bytes.slice(at,at+4)),size=view.getUint32(at+4,true);
      if(size>end-at-8)return false;
      if((type==='VP8 '&&size>10)||(type==='VP8L'&&size>5))hasPayload=true;
      // An animation frame header is metadata, not a decoded image payload.
      if(type==='ANMF'&&allowFrames){
        if(size<=16||!chunks(at+24,at+8+size,false))return false;
        hasPayload=true;
      }
      at+=8+size+(size&1);
    }
    return at===end&&hasPayload;
  };
  return chunks(12,bytes.length,true);
}
function completeMp4(bytes:Uint8Array):boolean {
  if(bytes.length<40)return false;
  const view=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  const child=(start:number,end:number,name:string):{start:number;end:number}|null=>{
    let at=start;
    while(at+8<=end){
      let size=view.getUint32(at),header=8;
      const type=String.fromCharCode(...bytes.slice(at+4,at+8));
      if(size===1){if(at+16>end)return null;size=Number(view.getBigUint64(at+8));header=16;}
      if(size===0)size=end-at;
      if(!Number.isSafeInteger(size)||size<header||size>end-at)return null;
      if(type===name)return {start:at+header,end:at+size};
      at+=size;
    }
    return null;
  };
  let at=0,ftyp=false,media=false,moov:{start:number;end:number}|null=null;
  while(at+8<=bytes.length){
    let size=view.getUint32(at),header=8;
    const type=String.fromCharCode(...bytes.slice(at+4,at+8));
    if(size===1){if(at+16>bytes.length)return false;size=Number(view.getBigUint64(at+8));header=16;}
    if(size===0)size=bytes.length-at;
    if(!Number.isSafeInteger(size)||size<header||size>bytes.length-at)return false;
    if(type==='ftyp'&&size>=16)ftyp=true;
    if(type==='moov'&&size>header+8)moov={start:at+header,end:at+size};
    if(type==='mdat'&&size>header)media=true;
    at+=size;
  }
  if(at!==bytes.length||!ftyp||!media||!moov)return false;
  let box:{start:number;end:number}|null=moov;
  for(const name of ['trak','mdia','minf','stbl','stsd']){
    box=child(box.start,box.end,name);if(!box)return false;
  }
  const sampleStart=box.start+8;
  if(box.end-sampleStart<8||view.getUint32(box.start+4)<1)return false;
  const sampleSize=view.getUint32(sampleStart);
  return sampleSize>=8&&sampleSize<=box.end-sampleStart;
}
function completeWebm(bytes:Uint8Array):boolean {
  if(bytes.length<40)return false;
  type Element={id:number;start:number;end:number};
  const elements=(start:number,end:number):Element[]|null=>{
    const out:Element[]=[];let at=start;
    while(at<end){
      const idStart=at,first=bytes[at];if(!first)return null;
      let idLength=1,mask=0x80;
      while(!(first&mask)){idLength++;mask>>=1;if(idLength>4)return null;}
      if(at+idLength>=end)return null;
      let id=0;for(let i=0;i<idLength;i++)id=id*256+bytes[at++];
      const sizeFirst=bytes[at];if(!sizeFirst)return null;
      let sizeLength=1,sizeMask=0x80;
      while(!(sizeFirst&sizeMask)){sizeLength++;sizeMask>>=1;if(sizeLength>8)return null;}
      if(at+sizeLength>end)return null;
      let size=BigInt(bytes[at++]&(sizeMask-1));
      for(let i=1;i<sizeLength;i++)size=(size<<8n)|BigInt(bytes[at++]);
      const unknown=size===(1n<<BigInt(7*sizeLength))-1n;
      if(unknown&&id!==0x18538067&&id!==0x1f43b675)return null;
      if(size>BigInt(end-at)&&!unknown)return null;
      const next=unknown?end:at+Number(size);
      if(next<=idStart||out.length>=8192)return null;
      out.push({id,start:at,end:next});at=next;
    }
    return at===end?out:null;
  };
  const top=elements(0,bytes.length);if(!top)return false;
  const header=top.find(e=>e.id===0x1a45dfa3),segment=top.find(e=>e.id===0x18538067);
  if(!header||!segment||header.end>segment.start)return false;
  const headerParts=elements(header.start,header.end);
  const docType=headerParts?.find(e=>e.id===0x4282);
  if(!docType||docType.end-docType.start!==4||String.fromCharCode(...bytes.slice(docType.start,docType.end))!=='webm')return false;
  const segmentParts=elements(segment.start,segment.end);if(!segmentParts)return false;
  const tracks=segmentParts.find(e=>e.id===0x1654ae6b);
  const trackEntries=tracks?elements(tracks.start,tracks.end):null;
  let videoTrack=false;
  for(const entry of trackEntries??[]){
    if(entry.id!==0xae)continue;
    const fields=elements(entry.start,entry.end);if(!fields)continue;
    const type=fields.find(e=>e.id===0x83),video=fields.find(e=>e.id===0xe0);
    if(!type||type.end-type.start!==1||bytes[type.start]!==1||!video)continue;
    const videoFields=elements(video.start,video.end);if(!videoFields)continue;
    const dimension=(id:number)=>{
      const field=videoFields.find(e=>e.id===id);if(!field||field.end-field.start<1||field.end-field.start>4)return 0;
      let value=0;for(let at=field.start;at<field.end;at++)value=value*256+bytes[at];return value;
    };
    const width=dimension(0xb0),height=dimension(0xba);
    if(width>0&&height>0&&width<=8192&&height<=8192&&width*height<=MAX_STORY_PIXELS)videoTrack=true;
  }
  if(!videoTrack)return false;
  for(const cluster of segmentParts.filter(e=>e.id===0x1f43b675)){
    const blocks=elements(cluster.start,cluster.end);if(!blocks)continue;
    if(blocks.some(e=>e.id===0xa3&&e.end-e.start>=5))return true;
    for(const group of blocks.filter(e=>e.id===0xa0)){
      const parts=elements(group.start,group.end);
      if(parts?.some(e=>e.id===0xa1&&e.end-e.start>=5))return true;
    }
  }
  return false;
}

export function hasStoryMediaSignature(bytes:Uint8Array,mime:string):boolean {
  if(mime==='image/png'||mime==='image/jpeg'||mime==='image/webp'){
    const dims=storyImageDimensions(bytes);
    return sniffStoryMime(bytes,'image')===mime&&!!dims&&dims.width>0&&dims.height>0&&
      dims.width<=8192&&dims.height<=8192&&dims.width*dims.height<=MAX_STORY_PIXELS&&
      (mime==='image/png'?completePng(bytes):mime==='image/jpeg'?completeJpeg(bytes):completeWebp(bytes));
  }
  if(mime==='video/mp4')return completeMp4(bytes);
  if(mime==='video/webm')return completeWebm(bytes);
  return false;
}

function sniffStoryMime(bytes:Uint8Array,kind:StoryMediaKind):string|null {
  if(kind==='image'){
    if(bytes[0]===137&&bytes[1]===80&&bytes[2]===78&&bytes[3]===71)return 'image/png';
    if(bytes[0]===0xff&&bytes[1]===0xd8)return 'image/jpeg';
    if(bytes.length>=12&&String.fromCharCode(...bytes.slice(0,4))==='RIFF'&&
       String.fromCharCode(...bytes.slice(8,12))==='WEBP')return 'image/webp';
  } else {
    if(bytes.length>=12&&String.fromCharCode(...bytes.slice(4,8))==='ftyp')return 'video/mp4';
    if(bytes[0]===0x1a&&bytes[1]===0x45&&bytes[2]===0xdf&&bytes[3]===0xa3)return 'video/webm';
  }
  return null;
}

/** Stream with an explicit cap before creating a blob or decoder. */
export async function readBoundedStoryMedia(src:string,kind:StoryMediaKind,signal:AbortSignal):Promise<{bytes:Uint8Array;mime:string}> {
  if(src.startsWith('data:')){
    const bytes=decodeStoryDataUri(src,kind);
    if(!bytes)throw Error('Story media data is invalid');
    return {bytes,mime:src.slice(5,src.indexOf(';')).toLowerCase()};
  }
  const response=await fetch(src,{signal,cache:'no-store',credentials:'omit'});
  if(!response.ok||!response.body)throw Error('Story media could not be read');
  const limit=kind==='image'?MAX_STORY_IMAGE_BYTES:MAX_STORY_VIDEO_BYTES;
  const declared=Number(response.headers.get('content-length'));
  if(Number.isFinite(declared)&&declared>limit){await response.body.cancel();throw Error('Story media exceeds byte budget');}
  const reader=response.body.getReader();
  const chunks:Uint8Array[]=[];let size=0;
  try{
    while(true){
      const result=await reader.read();if(result.done)break;
      size+=result.value.byteLength;
      if(size>limit)throw Error('Story media exceeds byte budget');
      chunks.push(result.value);
    }
  }catch(error){void reader.cancel();throw error;}
  const bytes=new Uint8Array(size);let offset=0;
  for(const chunk of chunks){bytes.set(chunk,offset);offset+=chunk.length;}
  const rawMime=response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase()??'';
  const claimed=rawMime==='image/jpg'?'image/jpeg':rawMime;
  const mime=sniffStoryMime(bytes,kind);
  if(!mime||(claimed&&claimed!=='application/octet-stream'&&claimed!==mime)||
     !hasStoryMediaSignature(bytes,mime))throw Error('Story media format or dimensions are invalid');
  return {bytes,mime};
}
